import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import {
  ArrowLeft,
  Play,
  Pause,
  Download,
  Share2,
  Trash2,
  Video,
  Clock,
  Calendar,
  FolderOpen,
  X,
  Volume2,
  VolumeX,
  MessageSquare,
  Phone,
  Send,
  Archive,
  ShoppingCart,
  Eye,
  Image,
  FileText,
  CheckSquare,
  Square,
  DownloadCloud,
  Folder
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Library() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  
  // Download center state
  const [showDownloadCenter, setShowDownloadCenter] = useState(false);
  const [selectedForDownload, setSelectedForDownload] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [downloadFilter, setDownloadFilter] = useState("all"); // all, videos, photos, content

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/projects`, { headers });
      setProjects(response.data);
    } catch (error) {
      toast.error("Failed to load library");
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async (projectId, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this video permanently?")) return;
    
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/projects/${projectId}`, { headers });
      setProjects(projects.filter(p => p.id !== projectId));
      if (selectedVideo?.id === projectId) {
        setSelectedVideo(null);
      }
      toast.success("Video deleted");
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const archiveProject = async (projectId, e) => {
    e?.stopPropagation();
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/projects/${projectId}/archive`, {}, { headers });
      toast.success("Video archived");
      fetchProjects();
    } catch (error) {
      toast.error("Failed to archive");
    }
  };

  const sendText = (project) => {
    // Open SMS/text dialog
    const phone = prompt("Enter phone number to send video link:");
    if (phone) {
      toast.success(`Text message queued to ${phone}`);
      // In production, this would call the broadcast API
    }
  };

  const sendWhatsApp = (project) => {
    const phone = prompt("Enter WhatsApp number:");
    if (phone) {
      toast.success(`WhatsApp message queued to ${phone}`);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0s";
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  // Download functions
  const toggleSelectForDownload = (projectId) => {
    setSelectedForDownload(prev => 
      prev.includes(projectId) 
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  };

  const selectAllForDownload = () => {
    const downloadableProjects = projects.filter(p => p.video_url);
    if (selectedForDownload.length === downloadableProjects.length) {
      setSelectedForDownload([]);
    } else {
      setSelectedForDownload(downloadableProjects.map(p => p.id));
    }
  };

  const downloadSelected = async () => {
    if (selectedForDownload.length === 0) {
      toast.error("Select items to download");
      return;
    }
    
    toast.success(`Downloading ${selectedForDownload.length} item(s)...`);
    
    for (const projectId of selectedForDownload) {
      const project = projects.find(p => p.id === projectId);
      if (project?.video_url) {
        const link = document.createElement('a');
        link.href = project.video_url.startsWith('http') 
          ? project.video_url 
          : `${process.env.REACT_APP_BACKEND_URL}${project.video_url}`;
        link.download = `${project.title || 'video'}.mp4`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    setSelectedForDownload([]);
    setSelectMode(false);
  };

  const downloadSingle = (project) => {
    if (!project.video_url) {
      toast.error("No video available for download");
      return;
    }
    const link = document.createElement('a');
    link.href = project.video_url.startsWith('http') 
      ? project.video_url 
      : `${process.env.REACT_APP_BACKEND_URL}${project.video_url}`;
    link.download = `${project.title || 'video'}.mp4`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Download started!");
  };

  const openVideo = (project) => {
    setSelectedVideo(project);
    setIsPlaying(true);
  };

  const closeVideo = () => {
    setSelectedVideo(null);
    setIsPlaying(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/dashboard")}
              className="text-text-secondary hover:text-text-primary"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </div>
          <span className="font-heading font-bold text-text-primary">Video Library</span>
          <div className="flex items-center gap-2">
            {/* Download Center Toggle */}
            <Button 
              variant={selectMode ? "default" : "outline"}
              size="sm"
              className={selectMode ? "bg-accent-warning text-black" : "border-border"}
              onClick={() => {
                setSelectMode(!selectMode);
                if (selectMode) setSelectedForDownload([]);
              }}
            >
              <DownloadCloud className="w-4 h-4 mr-1" />
              {selectMode ? "Cancel" : "Download"}
            </Button>
            <Button 
              className="bg-primary hover:bg-primary-hover text-white"
              onClick={() => navigate("/create")}
            >
              Create New
            </Button>
          </div>
        </div>

        {/* Download Selection Bar */}
        <AnimatePresence>
          {selectMode && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t border-border bg-background-paper"
            >
              <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={selectAllForDownload}
                    className="text-text-secondary"
                  >
                    {selectedForDownload.length === projects.filter(p => p.video_url).length ? (
                      <CheckSquare className="w-4 h-4 mr-2 text-primary" />
                    ) : (
                      <Square className="w-4 h-4 mr-2" />
                    )}
                    Select All
                  </Button>
                  <span className="text-sm text-text-muted">
                    {selectedForDownload.length} selected
                  </span>
                </div>
                
                {/* Download Filter Tabs */}
                <div className="flex items-center gap-1 bg-background-subtle rounded-lg p-1">
                  {[
                    { value: "all", label: "All", icon: Folder },
                    { value: "videos", label: "Videos", icon: Video },
                    { value: "photos", label: "Photos", icon: Image },
                    { value: "content", label: "Content", icon: FileText }
                  ].map(filter => (
                    <button
                      key={filter.value}
                      onClick={() => setDownloadFilter(filter.value)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium transition-all ${
                        downloadFilter === filter.value 
                          ? "bg-primary text-white" 
                          : "text-text-muted hover:text-text-primary"
                      }`}
                    >
                      <filter.icon className="w-3 h-3" />
                      {filter.label}
                    </button>
                  ))}
                </div>

                <Button
                  size="sm"
                  disabled={selectedForDownload.length === 0}
                  className="bg-accent-success hover:bg-accent-success/90 text-white"
                  onClick={downloadSelected}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download ({selectedForDownload.length})
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="loading-spinner w-8 h-8" />
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-20">
            <FolderOpen className="w-16 h-16 text-text-muted mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-text-primary mb-2">No videos yet</h3>
            <p className="text-text-secondary mb-6">Create your first video to see it here</p>
            <Button 
              className="bg-primary hover:bg-primary-hover text-white"
              onClick={() => navigate("/create")}
            >
              Create Video
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card 
                  className={`bg-background-paper border-border hover:border-primary/50 transition-all cursor-pointer group overflow-hidden ${
                    selectMode && selectedForDownload.includes(project.id) ? 'ring-2 ring-primary' : ''
                  }`}
                  onClick={() => selectMode ? toggleSelectForDownload(project.id) : openVideo(project)}
                >
                  <CardContent className="p-0">
                    {/* Thumbnail */}
                    <div className="aspect-video bg-background-subtle relative overflow-hidden">
                      {project.video_url ? (
                        <video
                          src={project.video_url.startsWith('http') ? project.video_url : `${process.env.REACT_APP_BACKEND_URL}${project.video_url}`}
                          className="w-full h-full object-cover"
                          muted
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/20 to-accent-neon/10">
                          <Video className="w-12 h-12 text-text-muted" />
                        </div>
                      )}
                      
                      {/* Select checkbox overlay */}
                      {selectMode && (
                        <div className="absolute top-2 left-2 z-10">
                          <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                            selectedForDownload.includes(project.id)
                              ? 'bg-primary border-primary'
                              : 'bg-black/50 border-white/50'
                          }`}>
                            {selectedForDownload.includes(project.id) && (
                              <CheckSquare className="w-4 h-4 text-white" />
                            )}
                          </div>
                        </div>
                      )}

                      {/* Play overlay - only show when not in select mode */}
                      {!selectMode && (
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                            <Play className="w-6 h-6 text-primary fill-primary ml-1" />
                          </div>
                        </div>
                      )}

                      {/* Duration badge */}
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        {formatDuration(project.target_duration)}
                      </div>

                      {/* Status badge */}
                      {!selectMode && (
                        <div className={`absolute top-2 left-2 px-2 py-1 rounded text-xs font-medium ${
                          project.status === 'completed' ? 'bg-accent-success/90 text-white' :
                          project.status === 'generating' ? 'bg-accent-warning/90 text-white' :
                          'bg-background/80 text-text-secondary'
                        }`}>
                          {project.status === 'completed' ? 'Ready' : 
                           project.status === 'generating' ? 'Processing' : 'Draft'}
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="p-4">
                      <h3 className="font-heading font-bold text-text-primary truncate mb-1">
                        {project.title}
                      </h3>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-text-muted">
                          <Calendar className="w-3 h-3" />
                          {formatDate(project.created_at)}
                        </div>
                        <div className="flex gap-1">
                          {/* Quick Download Button */}
                          {project.video_url && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-text-muted hover:text-accent-success"
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadSingle(project);
                              }}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-text-muted hover:text-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/broadcast/${project.id}`);
                            }}
                          >
                            <Share2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-text-muted hover:text-accent-error"
                            onClick={(e) => deleteProject(project.id, e)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Video Player Modal */}
      {selectedVideo && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={closeVideo}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="relative w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <Button
              variant="ghost"
              size="sm"
              className="absolute -top-12 right-0 text-white hover:bg-white/10"
              onClick={closeVideo}
            >
              <X className="w-5 h-5 mr-2" />
              Close
            </Button>

            {/* Video container */}
            <div className="bg-background-paper rounded-xl overflow-hidden">
              <div className="aspect-video bg-black relative">
                {selectedVideo.video_url ? (
                  <video
                    src={selectedVideo.video_url.startsWith('http') ? selectedVideo.video_url : `${process.env.REACT_APP_BACKEND_URL}${selectedVideo.video_url}`}
                    className="w-full h-full object-contain"
                    controls
                    autoPlay={isPlaying}
                    muted={isMuted}
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-text-muted">
                    <Video className="w-16 h-16 mb-4" />
                    <p className="text-lg">Video not yet generated</p>
                    <p className="text-sm mt-2">Go to studio to create the video</p>
                    <Button
                      className="mt-4 bg-primary hover:bg-primary-hover text-white"
                      onClick={() => navigate(`/studio/${selectedVideo.id}`)}
                    >
                      Open in Studio
                    </Button>
                  </div>
                )}
              </div>

              {/* Video info */}
              <div className="p-4 border-t border-border">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="font-heading font-bold text-xl text-text-primary">
                      {selectedVideo.title}
                    </h2>
                    <p className="text-sm text-text-muted mt-1">
                      {selectedVideo.description || "No description"}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-text-muted">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDuration(selectedVideo.target_duration)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(selectedVideo.created_at)}
                      </span>
                      <span className="capitalize px-2 py-0.5 rounded bg-background-subtle">
                        {selectedVideo.video_type}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    <Button
                      size="sm"
                      className="bg-primary hover:bg-primary-hover text-white"
                      onClick={() => navigate(`/broadcast/${selectedVideo.id}`)}
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Broadcast
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-accent-success text-accent-success hover:bg-accent-success/10"
                      onClick={() => sendText(selectedVideo)}
                    >
                      <Phone className="w-4 h-4 mr-1" />
                      Text
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-green-500 text-green-500 hover:bg-green-500/10"
                      onClick={() => sendWhatsApp(selectedVideo)}
                    >
                      <MessageSquare className="w-4 h-4 mr-1" />
                      WhatsApp
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-border"
                      onClick={() => navigate(`/studio/${selectedVideo.id}`)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-2">
                    {selectedVideo.video_url && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-border"
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = selectedVideo.video_url.startsWith('http') ? selectedVideo.video_url : `${process.env.REACT_APP_BACKEND_URL}${selectedVideo.video_url}`;
                          link.download = `${selectedVideo.title}.mp4`;
                          link.click();
                        }}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-accent-warning text-accent-warning hover:bg-accent-warning/10"
                      onClick={(e) => archiveProject(selectedVideo.id, e)}
                    >
                      <Archive className="w-4 h-4 mr-1" />
                      Archive
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-accent-error text-accent-error hover:bg-accent-error/10"
                      onClick={(e) => {
                        deleteProject(selectedVideo.id, e);
                        closeVideo();
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
